import React, { useState, useEffect, useMemo } from 'react';
import * as rrule from 'rrule';
import { useTranslation } from 'localization';
import TimePickerAtom from '../../atoms/TimePicker/TimePickerAtom';
import EndConditionAtom, { EndCondition } from '../../atoms/EndCondition/EndConditionAtom';
import SegmentedControlAtom, { SegmentedControlOption } from '../../atoms/SegmentedControl/SegmentedControlAtom';
import FormSectionAtom from '../../atoms/FormSection/FormSectionAtom';
import FormRowAtom from '../../atoms/FormRow/FormRowAtom';
import PrimeReactSingleSelect, { OptionType } from '../../atoms/single-select-dropdown/single-select-dropdown';
import InputNumberAtom from '../../atoms/InputNumber/InputNumberAtom';
import DatePicker from '../../atoms/DatePicker/DatePickerAtom';
import './SchedulesEditorMolecule.scss';
import {
  Frequency,
  Weekday,
  WeekdayOfYearValue,
  DayOfYearValue,
  WeekdayOfMonthValue,
  MonthlyMode,
  YearlyMode,
  Ordinal,
  Month,
} from 'types/schedule';
import moment from 'moment-timezone';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { validateRrule } from '@center-platform/materialization';

export interface SchedulesEditorProps {
  value?: { rrule: string; dtstart?: string };
  className?: string;
  label?: string;
  onScheduleChange?: (value: { rrule: string; dtstart?: string; timezone?: string }) => void;
  editable?: boolean;
  /** When true, automatically applies changes without showing Apply button */
  autoApply?: boolean;
  /** Called after Apply Changes is clicked and changes are successfully applied */
  onApplyComplete?: () => void;
}

// Helper type to track the complete state
interface ScheduleState {
  frequency: Frequency;
  interval: number;
  weekdays: Weekday[];
  time: string;
  zone: string;
  date: Date | null;
  monthlyMode: MonthlyMode;
  yearlyMode: YearlyMode;
  endCondition: EndCondition;
  dayOfMonth: number;
  weekdayOfMonth: WeekdayOfMonthValue;
  dayOfYear: DayOfYearValue;
  weekdayOfYear: WeekdayOfYearValue;
}

// Note: Using OptionType from PrimeReactSingleSelect ({ name, code })

export const SchedulesEditorMolecule: React.FC<SchedulesEditorProps> = ({
  className = '',
  label = 'Edit Schedule',
  onScheduleChange,
  value,
  editable = true,
  autoApply = false,
  onApplyComplete,
}) => {
  const { t, i18n } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [previewDates, setPreviewDates] = useState<Date[]>([]);

  // Helper to parse RRULE with optional dtstart
  function getParsedOpts(value?: { rrule: string; dtstart?: string }) {
    if (!value?.rrule) return undefined;

    try {
      let rruleStr = value.rrule;

      // Clean rrule string - remove any DTSTART and RRULE prefix
      rruleStr = rruleStr.replace(/DTSTART[^;]*;?/gi, '');
      rruleStr = rruleStr.replace(/^RRULE:/, '');

      // If dtstart is provided, parse with it
      if (value.dtstart) {
        const dtstart = new Date(value.dtstart);
        if (!isNaN(dtstart.getTime())) {
          const dtStartStr = dtstart
            .toISOString()
            .replace(/[-:]/g, '')
            .replace(/\.\d{3}/, '');
          return rrule.rrulestr(`DTSTART:${dtStartStr}\nRRULE:${rruleStr}`).options;
        }
      }

      // Parse rrule only
      return rrule.rrulestr(`RRULE:${rruleStr}`).options;
    } catch (e) {
      console.warn('Failed to parse RRULE:', value, e);
      return undefined;
    }
  }

  function getWeekdayNumber(wd: unknown): number {
    // rrule.Weekday objects have a .weekday, numbers are 0-6
    if (typeof wd === 'number') return wd;
    if (wd && typeof wd === 'object' && 'weekday' in wd && typeof (wd as any).weekday === 'number') {
      return (wd as any).weekday;
    }
    // fallback to Monday
    return 0;
  }

  const handlePreview = () => {
    console.log('[SchedulesEditorMolecule] handlePreview called');
    console.log('[SchedulesEditorMolecule] handlePreview - date state:', date);
    console.log('[SchedulesEditorMolecule] handlePreview - time state:', time);
    console.log('[SchedulesEditorMolecule] handlePreview - zone state:', zone);

    // Build dtstart in the selected timezone using moment-timezone
    // This ensures RRule interprets the time correctly for the timezone
    let dtstart: Date;
    if (date && time) {
      const [h, m] = time.split(':').map(Number);
      console.log('[SchedulesEditorMolecule] handlePreview - parsed h:', h, 'm:', m);
      // Create a moment in the target timezone with the selected date/time
      const mDate = moment.tz({
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
        hour: h,
        minute: m,
        second: 0,
      }, zone);
      console.log('[SchedulesEditorMolecule] handlePreview - mDate:', mDate.format());
      dtstart = mDate.toDate();
      console.log('[SchedulesEditorMolecule] handlePreview - dtstart:', dtstart);
    } else if (date) {
      dtstart = new Date(date);
    } else {
      dtstart = new Date();
    }

    // For preview, DON'T use byhour/byminute - the dtstart already has the correct time
    // Using byhour/byminute with tzid causes timezone double-conversion issues
    const opts: Partial<rrule.Options> = {
      dtstart,
      interval,
      // Don't use tzid for preview - dtstart is already in correct timezone
      // tzid causes issues with byhour/byminute interpretation
      ...getRRuleFrequencyOptions(),
    };

    // For preview, limit to 5 occurrences to avoid expensive computation
    const previewLimit = 5;
    if (endCondition.type === 'after') {
      opts.count = Math.min(endCondition.count, previewLimit);
    } else {
      opts.count = previewLimit;
    }

    console.log('[SchedulesEditorMolecule] handlePreview - opts:', opts);

    const rule = new rrule.RRule(opts as rrule.Options);
    console.log('[SchedulesEditorMolecule] handlePreview - rule.toString():', rule.toString());
    const dates = rule.all();
    console.log('[SchedulesEditorMolecule] handlePreview - generated dates:', dates);
    setPreviewDates(dates);
    setShowPreview(true);
  };

  /**
   * Format a preview date in the selected timezone.
   * With tzid set, RRule returns UTC dates that represent the correct instant.
   * We convert to the selected timezone for display.
   */
  const formatPreviewDate = (d: Date): string => {
    // Render preview dates in the user's current locale so fr-FR sees
    // "3 févr. 2026, 17:00" and en-US sees "Feb 3, 2026, 5:00 PM". Explicit
    // locale — the runtime default may not match the user's chosen language.
    const m = moment(d).tz(zone).locale(i18n.language);
    return m.format('lll z');
  };

  const defaultTimezone = moment.tz.guess();

  const [frequency, setFrequency] = useState<Frequency>('Daily');
  const [interval, setInterval] = useState<number>(1);
  const [weekdays, setWeekdays] = useState<Weekday[]>([]);
  const [time, setTime] = useState<string>('00:00');
  const [zone, setZone] = useState<string>(defaultTimezone);
  const [date, setDate] = useState<Date | null>(new Date());
  const [monthlyMode, setMonthlyMode] = useState<MonthlyMode>('dayOfMonth');
  const [yearlyMode, setYearlyMode] = useState<'dayOfYear' | 'weekdayOfYear'>('dayOfYear');
  const [endCondition, setEndCondition] = useState<EndCondition>({ type: 'never' });
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [weekdayOfMonth, setWeekdayOfMonth] = useState<WeekdayOfMonthValue>({
    ordinal: 'First',
    weekdays: [],
  });
  const [isInitialised, setIsInitialised] = useState(false);
  const [initialState, setInitialState] = useState<ScheduleState | null>(null);
  // Track the last rrule we processed to avoid re-init on equivalent values
  const [lastProcessedRrule, setLastProcessedRrule] = useState<string | undefined>(undefined);

  function hasN(obj: unknown): obj is { n: number } {
    return obj !== null && typeof obj === 'object' && 'n' in obj && typeof (obj as any).n === 'number';
  }

  const [dayOfYear, setDayOfYear] = useState<DayOfYearValue>({ month: 'January', day: 1 });
  const [weekdayOfYear, setWeekdayOfYear] = useState<WeekdayOfYearValue>({
    ordinal: 'First',
    weekdays: [],
    month: 'January',
  });

  // Options for dropdowns and segmented controls
  // Dropdown options using OptionType format ({ name, code })
  const frequencyOptions: OptionType[] = [
    { code: 'Daily', name: t('scheduleEditor.frequency.daily') },
    { code: 'Weekly', name: t('scheduleEditor.frequency.weekly') },
    { code: 'Monthly', name: t('scheduleEditor.frequency.monthly') },
    { code: 'Yearly', name: t('scheduleEditor.frequency.yearly') },
  ];

  const weekdayOptions: SegmentedControlOption<Weekday>[] = [
    { value: 'Sunday', label: t('scheduleEditor.week.sunday'), shortLabel: t('scheduleEditor.week.sunShort') },
    { value: 'Monday', label: t('scheduleEditor.week.monday'), shortLabel: t('scheduleEditor.week.monShort') },
    { value: 'Tuesday', label: t('scheduleEditor.week.tuesday'), shortLabel: t('scheduleEditor.week.tueShort') },
    { value: 'Wednesday', label: t('scheduleEditor.week.wednesday'), shortLabel: t('scheduleEditor.week.wedShort') },
    { value: 'Thursday', label: t('scheduleEditor.week.thursday'), shortLabel: t('scheduleEditor.week.thuShort') },
    { value: 'Friday', label: t('scheduleEditor.week.friday'), shortLabel: t('scheduleEditor.week.friShort') },
    { value: 'Saturday', label: t('scheduleEditor.week.saturday'), shortLabel: t('scheduleEditor.week.satShort') },
  ];

  const monthlyModeOptions: SegmentedControlOption<MonthlyMode>[] = [
    { value: 'dayOfMonth', label: t('scheduleEditor.byDayOfMonth') },
    { value: 'weekdayOfMonth', label: t('scheduleEditor.byWeekdayOfMonth') },
    { value: 'lastDayOfMonth', label: t('scheduleEditor.lastDayOfMonth') },
  ];

  const yearlyModeOptions: SegmentedControlOption<YearlyMode>[] = [
    { value: 'dayOfYear', label: t('scheduleEditor.byDate') },
    { value: 'weekdayOfYear', label: t('scheduleEditor.byWeekdayOfMonth') },
  ];

  const ordinalOptions: OptionType[] = [
    { code: 'First', name: t('scheduleEditor.ordinal.first') },
    { code: 'Second', name: t('scheduleEditor.ordinal.second') },
    { code: 'Third', name: t('scheduleEditor.ordinal.third') },
    { code: 'Fourth', name: t('scheduleEditor.ordinal.fourth') },
    { code: 'Last', name: t('scheduleEditor.ordinal.last') },
  ];

  const allMonths: Month[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const monthOptions: OptionType[] = allMonths.map((m) => ({
    code: m,
    name: t(`scheduleEditor.months.${m}`, { defaultValue: m }),
  }));

  const getCurrentState = (): ScheduleState => ({
    frequency,
    interval,
    weekdays: [...weekdays],
    time,
    zone,
    date,
    monthlyMode,
    yearlyMode,
    endCondition: { ...endCondition },
    dayOfMonth,
    weekdayOfMonth: { ...weekdayOfMonth, weekdays: [...weekdayOfMonth.weekdays] },
    dayOfYear: { ...dayOfYear },
    weekdayOfYear: { ...weekdayOfYear, weekdays: [...weekdayOfYear.weekdays] },
  });

  const statesEqual = (state1: ScheduleState, state2: ScheduleState): boolean => {
    const checks = {
      frequency: state1.frequency === state2.frequency,
      interval: state1.interval === state2.interval,
      weekdays: JSON.stringify(state1.weekdays) === JSON.stringify(state2.weekdays),
      time: state1.time === state2.time,
      zone: state1.zone === state2.zone,
      date: state1.date?.getTime() === state2.date?.getTime(),
      monthlyMode: state1.monthlyMode === state2.monthlyMode,
      yearlyMode: state1.yearlyMode === state2.yearlyMode,
      endCondition: JSON.stringify(state1.endCondition) === JSON.stringify(state2.endCondition),
      dayOfMonth: state1.dayOfMonth === state2.dayOfMonth,
      weekdayOfMonth: JSON.stringify(state1.weekdayOfMonth) === JSON.stringify(state2.weekdayOfMonth),
      dayOfYear: JSON.stringify(state1.dayOfYear) === JSON.stringify(state2.dayOfYear),
      weekdayOfYear: JSON.stringify(state1.weekdayOfYear) === JSON.stringify(state2.weekdayOfYear),
    };

    return Object.values(checks).every(Boolean);
  };

  const hasChanges = useMemo(() => {
    if (!initialState || !isInitialised) {
      return false;
    }

    const currentState = getCurrentState();
    return !statesEqual(initialState, currentState);
  }, [
    frequency,
    interval,
    weekdays,
    time,
    zone,
    date,
    monthlyMode,
    yearlyMode,
    endCondition,
    dayOfMonth,
    weekdayOfMonth,
    dayOfYear,
    weekdayOfYear,
    initialState,
    isInitialised,
  ]);

  function numberToMonthCode(n: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return months[(n - 1) % 12] || 'January';
  }

  function rruleFreqToAppFreq(freq: number): Frequency {
    switch (freq) {
      case rrule.Frequency.DAILY:
        return 'Daily';
      case rrule.Frequency.WEEKLY:
        return 'Weekly';
      case rrule.Frequency.MONTHLY:
        return 'Monthly';
      case rrule.Frequency.YEARLY:
        return 'Yearly';
      default:
        return 'Daily';
    }
  }

  function weekdayNumToAppWeekday(num: number): Weekday {
    const map: Weekday[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return map[num] || 'Monday';
  }

  function dateToTimeString(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const mins = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${mins}`;
  }

  function numberToOrdinal(n?: number): string {
    switch (n) {
      case 1:
        return 'First';
      case 2:
        return 'Second';
      case 3:
        return 'Third';
      case 4:
        return 'Fourth';
      case -1:
        return 'Last';
      default:
        return 'First';
    }
  }

  function getScheduleErrors() {
    const errors: Record<string, string> = {};

    if (frequency === 'Weekly' && weekdays.length === 0) {
      errors.weekdays = t('scheduleEditor.validation.weekdaysRequired');
    }

    if (frequency === 'Monthly' && monthlyMode === 'weekdayOfMonth' && weekdayOfMonth.weekdays.length === 0) {
      errors.weekdayOfMonth = t('scheduleEditor.validation.weekdaysRequired');
    }

    if (frequency === 'Yearly' && yearlyMode === 'weekdayOfYear' && weekdayOfYear.weekdays.length === 0) {
      errors.weekdayOfYear = t('scheduleEditor.validation.weekdaysRequired');
    }

    if (endCondition.type === 'onDate' && !endCondition.date) {
      errors.endCondition = t('scheduleEditor.validation.endDateRequired');
    }

    // Assemble the RRULE we'd emit and run it through the shared validator so
    // the client enforces the same rules the graph-service does. Anything that
    // would 400 on save shows up as an inline field error here instead. Keyed
    // by the validator's `field` so UI controls (interval, until, timezone,
    // rrule) can attach their own message.
    try {
      const candidate = buildOutputValue();
      const rruleValidation = validateRrule({
        rrule: candidate.rrule,
        dtstart: candidate.dtstart,
        timezone: candidate.timezone,
      });
      if (rruleValidation.ok === false) {
        for (const err of rruleValidation.errors) {
          // Preserve the first message per field — if the user cleared it,
          // subsequent renders will surface any new violations naturally.
          if (!errors[err.field]) errors[err.field] = err.message;
        }
      }
    } catch {
      // buildOutputValue can throw if state is transiently invalid mid-edit;
      // in that case the user isn't in a save-ready state anyway and other
      // errors above will surface first.
    }

    return errors;
  }

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasAttemptedApply, setHasAttemptedApply] = useState(false);

  useEffect(() => {
    if (!showPreview) return;

    // Build dtstart in the selected timezone using moment-timezone
    let dtstart: Date;
    if (date && time) {
      const [h, m] = time.split(':').map(Number);
      const mDate = moment.tz({
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
        hour: h,
        minute: m,
        second: 0,
      }, zone);
      dtstart = mDate.toDate();
    } else if (date) {
      dtstart = new Date(date);
    } else {
      dtstart = new Date();
    }

    // For preview, DON'T use byhour/byminute - the dtstart already has the correct time
    // Using byhour/byminute with tzid causes timezone double-conversion issues
    const opts: Partial<rrule.Options> = {
      dtstart,
      interval,
      // Don't use tzid for preview - dtstart is already in correct timezone
      ...getRRuleFrequencyOptions(),
    };

    // For preview, limit to 5 occurrences to avoid expensive computation
    // Override any existing end condition for preview purposes only
    const previewLimit = 5;
    if (endCondition.type === 'after') {
      opts.count = Math.min(endCondition.count, previewLimit);
    } else {
      // For 'never' or 'onDate', force a count limit for preview
      opts.count = previewLimit;
      // Remove until so count takes precedence
      delete opts.until;
    }

    try {
      const rule = new rrule.RRule(opts as rrule.Options);
      const all = rule.all();
      setPreviewDates(all);
    } catch {
      setPreviewDates([]);
    }
  }, [
    showPreview,
    interval,
    frequency,
    weekdays,
    monthlyMode,
    dayOfMonth,
    weekdayOfMonth,
    yearlyMode,
    dayOfYear,
    weekdayOfYear,
    time,
    date,
    zone,
    endCondition,
  ]);

  useEffect(() => {
    console.log('[SchedulesEditorMolecule] ========== INIT useEffect ==========');
    console.log('[SchedulesEditorMolecule] Processing value:', value);
    console.log('[SchedulesEditorMolecule] value?.rrule:', value?.rrule);
    console.log('[SchedulesEditorMolecule] value?.dtstart:', value?.dtstart);
    console.log('[SchedulesEditorMolecule] lastProcessedRrule:', lastProcessedRrule);

    // Skip re-init if the rrule hasn't actually changed
    // This prevents infinite loops when auto-apply sends the same value back
    if (isInitialised && value?.rrule === lastProcessedRrule) {
      console.log('[SchedulesEditorMolecule] Skipping re-init - rrule unchanged');
      return;
    }

    const parsedOpts = getParsedOpts(value);
    console.log('[SchedulesEditorMolecule] parsedOpts:', parsedOpts);

    if (!parsedOpts) {
      // No RRULE provided - set defaults
      console.log('[SchedulesEditorMolecule] No parsed options, using defaults');

      const now = new Date();
      setFrequency('Daily');
      setInterval(1);
      setWeekdays([]);
      setTime('00:00');
      setZone(defaultTimezone);
      setDate(now);
      setMonthlyMode('dayOfMonth');
      setYearlyMode('dayOfYear');
      setEndCondition({ type: 'never' });
      setDayOfMonth(1);
      setWeekdayOfMonth({ ordinal: 'First', weekdays: [] });
      setDayOfYear({ month: 'January', day: 1 });
      setWeekdayOfYear({ ordinal: 'First', weekdays: [], month: 'January' });

      if (!isInitialised) {
        const defaultState: ScheduleState = {
          frequency: 'Daily',
          interval: 1,
          weekdays: [],
          time: '00:00',
          zone: defaultTimezone,
          date: now,
          monthlyMode: 'dayOfMonth',
          yearlyMode: 'dayOfYear',
          endCondition: { type: 'never' },
          dayOfMonth: 1,
          weekdayOfMonth: { ordinal: 'First', weekdays: [] },
          dayOfYear: { month: 'January', day: 1 },
          weekdayOfYear: { ordinal: 'First', weekdays: [], month: 'January' },
        };
        setInitialState(defaultState);
        setIsInitialised(true);
        setLastProcessedRrule(undefined);
      }
      return;
    }

    // Parse existing RRULE
    console.log('[SchedulesEditorMolecule] Parsing existing RRULE with options:', parsedOpts);

    const rruleStr = value?.rrule || '';
    const hasByDay = /BYDAY=/i.test(rruleStr);
    const hasByMonthDay = /BYMONTHDAY=/i.test(rruleStr);
    const hasByMonth = /BYMONTH=/i.test(rruleStr);

    function getYearlyState(opts: NonNullable<ReturnType<typeof getParsedOpts>>) {
      let yearlyMode: YearlyMode = 'dayOfYear';
      let weekdayOfYear: WeekdayOfYearValue = { ordinal: 'First', weekdays: [], month: 'January' };
      let dayOfYear: DayOfYearValue = { month: 'January', day: 1 };

      const getByWeekdayArr = () => {
        if (
          hasByDay &&
          hasByMonth &&
          typeof opts.byweekday !== 'undefined' &&
          ((Array.isArray(opts.byweekday) && opts.byweekday.length > 0) ||
            (!Array.isArray(opts.byweekday) && opts.byweekday !== null))
        ) {
          return Array.isArray(opts.byweekday) ? opts.byweekday : [opts.byweekday];
        }
        return [];
      };

      const getByNWeekdayArr = () => {
        if (hasByDay && hasByMonth && opts.bynweekday && Array.isArray(opts.bynweekday) && opts.bynweekday.length > 0) {
          return opts.bynweekday.map((entry) =>
            Array.isArray(entry) && entry.length === 2 ? new rrule.Weekday(entry[0], entry[1]) : entry
          );
        }
        return [];
      };

      const byweekdayArr = getByWeekdayArr();
      const bynweekdayArr = getByNWeekdayArr();

      if (hasByDay && hasByMonth && byweekdayArr.length > 0 && Array.isArray(opts.bymonth) && opts.bymonth.length > 0) {
        yearlyMode = 'weekdayOfYear';
        const ordinalNum = hasN(byweekdayArr[0]) ? byweekdayArr[0].n : 1;
        weekdayOfYear = {
          ordinal: numberToOrdinal(ordinalNum) as Ordinal,
          weekdays: byweekdayArr.map((wd) => weekdayNumToAppWeekday(getWeekdayNumber(wd))),
          month: numberToMonthCode(opts.bymonth[0]) as Month,
        };
      } else if (
        hasByDay &&
        hasByMonth &&
        bynweekdayArr.length > 0 &&
        Array.isArray(opts.bymonth) &&
        opts.bymonth.length > 0
      ) {
        yearlyMode = 'weekdayOfYear';
        const ordinalNum = hasN(bynweekdayArr[0]) ? bynweekdayArr[0].n : 1;
        weekdayOfYear = {
          ordinal: numberToOrdinal(ordinalNum) as Ordinal,
          weekdays: bynweekdayArr.map((wd) => weekdayNumToAppWeekday(getWeekdayNumber(wd))),
          month: numberToMonthCode(opts.bymonth[0]) as Month,
        };
      } else if (
        hasByMonth &&
        hasByMonthDay &&
        Array.isArray(opts.bymonth) &&
        opts.bymonth.length === 1 &&
        Array.isArray(opts.bymonthday) &&
        opts.bymonthday.length === 1
      ) {
        dayOfYear = {
          month: numberToMonthCode(opts.bymonth[0]) as Month,
          day: opts.bymonthday[0],
        };
      }
      return { yearlyMode, weekdayOfYear, dayOfYear };
    }

    function getMonthlyState(opts: NonNullable<ReturnType<typeof getParsedOpts>>) {
      let monthlyMode: MonthlyMode = 'dayOfMonth';
      let dayOfMonth = 1;
      let weekdayOfMonth: WeekdayOfMonthValue = { ordinal: 'First', weekdays: [] };

      const getByWeekdayArr = () => {
        if (
          hasByDay &&
          typeof opts.byweekday !== 'undefined' &&
          ((Array.isArray(opts.byweekday) && opts.byweekday.length > 0) ||
            (!Array.isArray(opts.byweekday) && opts.byweekday !== null))
        ) {
          return Array.isArray(opts.byweekday) ? opts.byweekday : [opts.byweekday];
        }
        return [];
      };
      const getByNWeekdayArr = () => {
        if (hasByDay && opts.bynweekday && Array.isArray(opts.bynweekday) && opts.bynweekday.length > 0) {
          return opts.bynweekday.map((entry: any) =>
            Array.isArray(entry) && entry.length === 2 ? new rrule.Weekday(entry[0], entry[1]) : entry
          );
        }
        return [];
      };

      const byweekdayArr = getByWeekdayArr();
      const bynweekdayArr = getByNWeekdayArr();

      if (hasByDay && byweekdayArr.length > 0) {
        monthlyMode = 'weekdayOfMonth';
        const ordinalNum = hasN(byweekdayArr[0]) ? byweekdayArr[0].n : 1;
        weekdayOfMonth = {
          ordinal: numberToOrdinal(ordinalNum) as Ordinal,
          weekdays: byweekdayArr.map((wd) => weekdayNumToAppWeekday(getWeekdayNumber(wd))),
        };
      } else if (hasByDay && bynweekdayArr.length > 0) {
        monthlyMode = 'weekdayOfMonth';
        const ordinalNum = hasN(bynweekdayArr[0]) ? bynweekdayArr[0].n : 1;
        weekdayOfMonth = {
          ordinal: numberToOrdinal(ordinalNum) as Ordinal,
          weekdays: bynweekdayArr.map((wd) => weekdayNumToAppWeekday(getWeekdayNumber(wd))),
        };
      } else if (hasByMonthDay) {
        // rrule.js stores positive month days in bymonthday and negative in bynmonthday
        const byMonthdayArr = Array.isArray(opts.bymonthday) && opts.bymonthday.length > 0
          ? opts.bymonthday
          : [];
        const bynMonthdayArr = Array.isArray((opts as any).bynmonthday) && (opts as any).bynmonthday.length > 0
          ? (opts as any).bynmonthday
          : [];

        if (bynMonthdayArr.length > 0 && bynMonthdayArr[0] === -1) {
          // Last day of month (BYMONTHDAY=-1 is stored in bynmonthday)
          monthlyMode = 'lastDayOfMonth';
        } else if (byMonthdayArr.length > 0) {
          dayOfMonth = byMonthdayArr[0];
        }
      }
      return { monthlyMode, dayOfMonth, weekdayOfMonth };
    }

    const getWeeklyState = (opts: NonNullable<ReturnType<typeof getParsedOpts>>) => {
      let weekdays: Weekday[] = [];
      if (!/BYDAY=/i.test(rruleStr || '')) return { weekdays };
      if (!opts.byweekday || (Array.isArray(opts.byweekday) && opts.byweekday.length === 0)) {
        return { weekdays };
      }
      if (Array.isArray(opts.byweekday)) {
        weekdays = opts.byweekday.map((wd) => weekdayNumToAppWeekday(getWeekdayNumber(wd)));
      } else {
        weekdays = [weekdayNumToAppWeekday(getWeekdayNumber(opts.byweekday))];
      }
      return { weekdays };
    };

    // Initialize variables for the parsed state
    const parsedFrequency = rruleFreqToAppFreq(parsedOpts.freq);
    const parsedInterval = parsedOpts.interval ?? 1;
    let parsedWeekdays: Weekday[] = [];
    let parsedTime = '00:00';
    const parsedZone = defaultTimezone;
    let parsedDate: Date | null = new Date();
    let parsedMonthlyMode: MonthlyMode = 'dayOfMonth';
    let parsedYearlyMode: YearlyMode = 'dayOfYear';
    let parsedEndCondition: EndCondition = { type: 'never' };
    let parsedDayOfMonth = 1;
    let parsedWeekdayOfMonth: WeekdayOfMonthValue = { ordinal: 'First', weekdays: [] };
    let parsedDayOfYear: DayOfYearValue = { month: 'January', day: 1 };
    let parsedWeekdayOfYear: WeekdayOfYearValue = { ordinal: 'First', weekdays: [], month: 'January' };

    // Parse specific frequency settings
    if (parsedOpts.freq === rrule.Frequency.YEARLY) {
      const { yearlyMode, weekdayOfYear, dayOfYear } = getYearlyState(parsedOpts);
      parsedYearlyMode = yearlyMode;
      parsedWeekdayOfYear = weekdayOfYear;
      parsedDayOfYear = dayOfYear;
    }

    if (parsedOpts.freq === rrule.Frequency.MONTHLY) {
      const { monthlyMode, dayOfMonth, weekdayOfMonth } = getMonthlyState(parsedOpts);
      parsedMonthlyMode = monthlyMode;
      parsedDayOfMonth = dayOfMonth;
      parsedWeekdayOfMonth = weekdayOfMonth;
    }

    if (parsedOpts.freq === rrule.Frequency.WEEKLY) {
      const { weekdays } = getWeeklyState(parsedOpts);
      parsedWeekdays = weekdays;
    }

    // Time and date parsing - handle dtstart from value prop
    if (value?.dtstart) {
      const dtStartDate = new Date(value.dtstart);
      if (!isNaN(dtStartDate.getTime())) {
        parsedDate = dtStartDate;
        parsedTime = dateToTimeString(dtStartDate);
      }
    } else if (parsedOpts.dtstart) {
      parsedDate = parsedOpts.dtstart;
      parsedTime = dateToTimeString(parsedOpts.dtstart);
    }

    // End condition parsing
    if (parsedOpts.until) {
      parsedEndCondition = { type: 'onDate', date: parsedOpts.until.toDateString() };
    } else if (parsedOpts.count) {
      parsedEndCondition = { type: 'after', count: parsedOpts.count };
    }

    // Set all the states
    setFrequency(parsedFrequency);
    setInterval(parsedInterval);
    setWeekdays(parsedWeekdays);
    setTime(parsedTime);
    setZone(parsedZone);
    setDate(parsedDate);
    setMonthlyMode(parsedMonthlyMode);
    setYearlyMode(parsedYearlyMode);
    setEndCondition(parsedEndCondition);
    setDayOfMonth(parsedDayOfMonth);
    setWeekdayOfMonth(parsedWeekdayOfMonth);
    setDayOfYear(parsedDayOfYear);
    setWeekdayOfYear(parsedWeekdayOfYear);

    // Set initial state with the parsed values
    if (!isInitialised) {
      const parsedState: ScheduleState = {
        frequency: parsedFrequency,
        interval: parsedInterval,
        weekdays: parsedWeekdays,
        time: parsedTime,
        zone: parsedZone,
        date: parsedDate,
        monthlyMode: parsedMonthlyMode,
        yearlyMode: parsedYearlyMode,
        endCondition: parsedEndCondition,
        dayOfMonth: parsedDayOfMonth,
        weekdayOfMonth: parsedWeekdayOfMonth,
        dayOfYear: parsedDayOfYear,
        weekdayOfYear: parsedWeekdayOfYear,
      };
      setInitialState(parsedState);
      setIsInitialised(true);
      setLastProcessedRrule(value?.rrule);
    }
  }, [value]);

  // Only show validation errors after user has attempted to apply changes
  useEffect(() => {
    if (hasAttemptedApply) {
      setErrors(getScheduleErrors());
    }
  }, [frequency, weekdays, monthlyMode, weekdayOfMonth, yearlyMode, weekdayOfYear, endCondition, hasAttemptedApply]);

  const weekdayMap: Record<Weekday, rrule.Weekday> = {
    Monday: new rrule.Weekday(0),
    Tuesday: new rrule.Weekday(1),
    Wednesday: new rrule.Weekday(2),
    Thursday: new rrule.Weekday(3),
    Friday: new rrule.Weekday(4),
    Saturday: new rrule.Weekday(5),
    Sunday: new rrule.Weekday(6),
  };

  function getRRuleFrequencyOptions() {
    switch (frequency) {
      case 'Daily':
        return { freq: rrule.Frequency.DAILY };
      case 'Weekly':
        return {
          freq: rrule.Frequency.WEEKLY,
          ...(weekdays.length ? { byweekday: weekdays.map((day) => weekdayMap[day]) } : {}),
        };
      case 'Monthly':
        return getMonthlyOptions();
      case 'Yearly':
        return getYearlyOptions();
      default:
        return {};
    }
  }

  function getMonthlyOptions() {
    if (monthlyMode === 'lastDayOfMonth') {
      return {
        freq: rrule.Frequency.MONTHLY,
        bymonthday: [-1],
      };
    }
    if (monthlyMode === 'dayOfMonth') {
      return {
        freq: rrule.Frequency.MONTHLY,
        bymonthday: [dayOfMonth],
      };
    }
    const wds = weekdayOfMonth.weekdays.length ? weekdayOfMonth.weekdays : ['Monday'];
    return {
      freq: rrule.Frequency.MONTHLY,
      byweekday: wds.map((w) => weekdayMap[w as Weekday].nth(ordinalToNumber(weekdayOfMonth.ordinal))),
    };
  }

  function getYearlyOptions() {
    if (yearlyMode === 'dayOfYear') {
      return {
        freq: rrule.Frequency.YEARLY,
        bymonth: [monthToNumber(dayOfYear.month)],
        bymonthday: [dayOfYear.day],
      };
    }
    if (weekdayOfYear.weekdays.length) {
      return {
        freq: rrule.Frequency.YEARLY,
        bymonth: [monthToNumber(weekdayOfYear.month)],
        byweekday: weekdayOfYear.weekdays.map((w) => weekdayMap[w].nth(ordinalToNumber(weekdayOfYear.ordinal))),
      };
    }
    return { freq: rrule.Frequency.YEARLY, bymonth: [monthToNumber(weekdayOfYear.month)] };
  }

  function monthToNumber(month: string): number {
    return allMonths.indexOf(month as Month) + 1 || 1;
  }

  function ordinalToNumber(ordinal: string): number {
    switch (ordinal) {
      case 'First':
        return 1;
      case 'Second':
        return 2;
      case 'Third':
        return 3;
      case 'Fourth':
        return 4;
      case 'Last':
        return -1;
      default:
        return 1;
    }
  }

  /**
   * Join a list of items with the current locale's list conjunction (English "and",
   * French "et", etc.). Uses Intl.ListFormat so we don't hardcode the separator.
   * Falls back to comma-join if the runtime lacks the API (very old jsdom in tests).
   */
  function localizedListJoin(items: string[]): string {
    try {
      // Intl.ListFormat is present in every modern Node/browser.
      return new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(items);
    } catch {
      return items.join(', ');
    }
  }

  function timeZoneString(): string {
    if (!time || time === '00:00') return '';
    const zoneSuffix = zone && zone !== 'Local time' ? ` ${zone}` : '';
    return ' ' + t('scheduleEditor.summary.atTime', 'at {{time}}{{zone}}', { time, zone: zoneSuffix });
  }

  function renderScheduleSummary(): string {
    const timeZoneSuffix = timeZoneString();
    const weekdayList = localizedListJoin(weekdays);

    let baseSummary = t('scheduleEditor.summary.custom', 'Custom schedule');
    if (frequency === 'Weekly') {
      baseSummary = t('scheduleEditor.summary.weekly', 'Every {{weekdays}}{{suffix}}', {
        weekdays: weekdayList,
        suffix: timeZoneSuffix,
      });
    } else if (frequency === 'Monthly' && monthlyMode === 'dayOfMonth') {
      baseSummary = t('scheduleEditor.summary.monthlyDay', 'Every month on day {{day}}{{suffix}}', {
        day: dayOfMonth,
        suffix: timeZoneSuffix,
      });
    } else if (frequency === 'Monthly' && monthlyMode === 'lastDayOfMonth') {
      baseSummary = t('scheduleEditor.summary.monthlyLastDay', 'Every month on the last day{{suffix}}', {
        suffix: timeZoneSuffix,
      });
    } else if (frequency === 'Monthly' && monthlyMode === 'weekdayOfMonth') {
      const timeOnly = time && time !== '00:00'
        ? ' ' + t('scheduleEditor.summary.atTimeShort', 'at {{time}}', { time })
        : '';
      // Cast the vars object to Record<string, unknown> to match t()'s
      // $Dictionary constraint. Without this widening TS narrows the literal
      // `defaultValue` string and rejects the whole options object.
      const monthlyWeekdayVars: Record<string, unknown> = {
        ordinal: String(weekdayOfMonth.ordinal),
        weekdays: localizedListJoin(weekdayOfMonth.weekdays),
        suffix: timeOnly,
        defaultValue: 'Every month on the {{ordinal}} {{weekdays}}{{suffix}}',
      };
      baseSummary = t('scheduleEditor.summary.monthlyWeekday', monthlyWeekdayVars);
    } else if (frequency === 'Yearly' && yearlyMode === 'dayOfYear') {
      const yearlyDayVars: Record<string, unknown> = {
        month: String(dayOfYear.month),
        day: dayOfYear.day,
        suffix: timeZoneSuffix,
        defaultValue: 'Every year on {{month}} {{day}}{{suffix}}',
      };
      baseSummary = t('scheduleEditor.summary.yearlyDay', yearlyDayVars);
    } else if (frequency === 'Yearly' && yearlyMode === 'weekdayOfYear') {
      const yearlyWeekdayVars: Record<string, unknown> = {
        ordinal: String(weekdayOfYear.ordinal),
        weekdays: localizedListJoin(weekdayOfYear.weekdays),
        month: String(weekdayOfYear.month),
        suffix: timeZoneSuffix,
        defaultValue: 'Every year on the {{ordinal}} {{weekdays}} of {{month}}{{suffix}}',
      };
      baseSummary = t('scheduleEditor.summary.yearlyWeekday', yearlyWeekdayVars);
    } else if (frequency === 'Daily') {
      baseSummary = t('scheduleEditor.summary.daily', 'Every day{{suffix}}', { suffix: timeZoneSuffix });
    }

    // Locale-aware date rendering — Intl.DateTimeFormat matches the app's
    // current language; toLocaleDateString() with no args picks the runtime
    // default which may not match.
    const startStr = date
      ? ' ' +
        t('scheduleEditor.summary.startingOn', 'starting {{date}}', {
          date: date.toLocaleDateString(i18n.language),
        })
      : '';

    let endStr = '';
    if (endCondition.type === 'onDate' && endCondition.date) {
      endStr =
        ', ' +
        t('scheduleEditor.summary.endsOn', 'ends on {{date}}', {
          date: new Date(endCondition.date).toLocaleDateString(i18n.language),
        });
    } else if (endCondition.type === 'after' && endCondition.count) {
      endStr =
        ', ' +
        t('scheduleEditor.summary.endsAfter', 'ends after {{count}} occurrences', {
          count: endCondition.count,
        });
    }

    return `${baseSummary}${startStr}${endStr}`;
  }

  /**
   * Build the output value from current state
   */
  const buildOutputValue = (): { rrule: string; dtstart?: string; timezone?: string } => {
    console.log('[SchedulesEditorMolecule] buildOutputValue called');
    console.log('[SchedulesEditorMolecule] buildOutputValue - frequency:', frequency);
    console.log('[SchedulesEditorMolecule] buildOutputValue - interval:', interval);
    console.log('[SchedulesEditorMolecule] buildOutputValue - weekdays:', weekdays);
    console.log('[SchedulesEditorMolecule] buildOutputValue - time:', time);
    console.log('[SchedulesEditorMolecule] buildOutputValue - date:', date);
    console.log('[SchedulesEditorMolecule] buildOutputValue - zone:', zone);

    const rruleObj: Partial<rrule.Options> = {
      interval,
      ...getRRuleFrequencyOptions(),
    };

    if (time) {
      const [hours, minutes] = time.split(':').map(Number);
      rruleObj.byhour = [hours];
      rruleObj.byminute = [minutes];
      rruleObj.bysecond = [0];
    }

    if (endCondition.type === 'onDate' && endCondition.date) {
      rruleObj.until = new Date(endCondition.date);
    } else if (endCondition.type === 'after' && endCondition.count) {
      rruleObj.count = endCondition.count;
    }

    console.log('[SchedulesEditorMolecule] buildOutputValue - rruleObj:', rruleObj);

    const ruleObj = new rrule.RRule(rruleObj as rrule.Options);
    const rruleString = ruleObj.toString();

    console.log('[SchedulesEditorMolecule] buildOutputValue - rruleString:', rruleString);

    const outputValue: { rrule: string; dtstart?: string; timezone?: string } = {
      rrule: rruleString,
    };

    if (date) {
      const dtstart = new Date(date);
      if (time) {
        const [hours, minutes] = time.split(':').map(Number);
        dtstart.setHours(hours, minutes, 0, 0);
      }
      outputValue.dtstart = dtstart.toISOString();
    }

    if (zone && zone !== 'Local time') {
      outputValue.timezone = zone;
    }

    console.log('[SchedulesEditorMolecule] buildOutputValue - outputValue:', outputValue);
    return outputValue;
  };

  const handleApplyChanges = () => {
    if (!editable) return;

    // Mark that user has attempted to apply - this triggers validation display
    setHasAttemptedApply(true);

    // Validate before applying
    const currentErrors = getScheduleErrors();
    setErrors(currentErrors);

    if (Object.keys(currentErrors).length > 0 || !hasChanges) {
      return;
    }

    console.log('🔍 [Apply Changes] Button clicked');

    const outputValue = buildOutputValue();

    if (typeof onScheduleChange === 'function') {
      // IMPORTANT: Update initialState BEFORE calling onScheduleChange
      // This immediately makes hasChanges return false
      const newState = getCurrentState();
      setInitialState(newState);

      // Reset validation state for next edit cycle
      setHasAttemptedApply(false);
      setErrors({});

      console.log('🔍 [Apply Changes] Calling onScheduleChange with:', outputValue);
      onScheduleChange(outputValue);

      // Notify parent that apply is complete (e.g., to switch tabs)
      if (typeof onApplyComplete === 'function') {
        onApplyComplete();
      }
    }
  };

  // Auto-apply: call onScheduleChange whenever values change (after initialization)
  useEffect(() => {
    console.log('[SchedulesEditorMolecule] Auto-apply useEffect triggered');
    console.log('[SchedulesEditorMolecule] autoApply:', autoApply);
    console.log('[SchedulesEditorMolecule] isInitialised:', isInitialised);
    console.log('[SchedulesEditorMolecule] onScheduleChange exists:', !!onScheduleChange);

    if (!autoApply || !isInitialised || !onScheduleChange) {
      console.log('[SchedulesEditorMolecule] Auto-apply skipped - conditions not met');
      return;
    }

    // Validate before auto-applying
    const currentErrors = getScheduleErrors();
    if (Object.keys(currentErrors).length > 0) {
      console.log('[SchedulesEditorMolecule] Auto-apply skipped - validation errors:', currentErrors);
      return;
    }

    console.log('[SchedulesEditorMolecule] Auto-apply - building output value');
    const outputValue = buildOutputValue();
    console.log('[SchedulesEditorMolecule] Auto-apply - calling onScheduleChange with:', outputValue);

    // Update lastProcessedRrule so we don't re-init from the value we just sent
    setLastProcessedRrule(outputValue.rrule);

    onScheduleChange(outputValue);
  }, [
    autoApply,
    isInitialised,
    frequency,
    interval,
    weekdays,
    time,
    zone,
    date,
    monthlyMode,
    yearlyMode,
    endCondition,
    dayOfMonth,
    weekdayOfMonth,
    dayOfYear,
    weekdayOfYear,
  ]);

  // Button is disabled only if there are no changes to apply
  // Validation errors are shown after clicking, not before
  const isApplyDisabled = !hasChanges;

  // Handler for frequency dropdown change (PrimeReactSingleSelect uses { name, code })
  const handleFrequencyChange = (e: { value: OptionType | null }) => {
    if (e.value) {
      setFrequency(e.value.code as Frequency);
    }
  };

  // Handler for ordinal dropdown change (monthly)
  const handleMonthlyOrdinalChange = (e: { value: OptionType | null }) => {
    if (e.value) {
      setWeekdayOfMonth({ ...weekdayOfMonth, ordinal: e.value.code as Ordinal });
    }
  };

  // Handler for ordinal dropdown change (yearly)
  const handleYearlyOrdinalChange = (e: { value: OptionType | null }) => {
    if (e.value) {
      setWeekdayOfYear({ ...weekdayOfYear, ordinal: e.value.code as Ordinal });
    }
  };

  // Handler for month dropdown change (yearly by date)
  const handleYearlyMonthChange = (e: { value: OptionType | null }) => {
    if (e.value) {
      setDayOfYear({ ...dayOfYear, month: e.value.code as Month });
    }
  };

  // Handler for month dropdown change (yearly by weekday)
  const handleYearlyWeekdayMonthChange = (e: { value: OptionType | null }) => {
    if (e.value) {
      setWeekdayOfYear({ ...weekdayOfYear, month: e.value.code as Month });
    }
  };

  // Selected options for dropdowns (using code to match)
  const selectedFrequency = frequencyOptions.find((f) => f.code === frequency) || null;
  const selectedMonthlyOrdinal = ordinalOptions.find((o) => o.code === weekdayOfMonth.ordinal) || null;
  const selectedYearlyOrdinal = ordinalOptions.find((o) => o.code === weekdayOfYear.ordinal) || null;
  const selectedYearlyMonth = monthOptions.find((m) => m.code === dayOfYear.month) || null;
  const selectedYearlyWeekdayMonth = monthOptions.find((m) => m.code === weekdayOfYear.month) || null;

  return (
    <div className={`schedules-editor ${className}`} data-testid="schedules-editor">
      {/* ZONE A: Anchors - Start date, time, timezone */}
      <FormSectionAtom variant="default" data-testid="zone-anchors">
        <FormRowAtom align="center" gap="large" wrap>
          <div className="schedules-editor__anchor-field" data-testid="start-date">
            <DatePicker
              value={date}
              onDateChange={(newDate) => newDate !== null && setDate(newDate)}
              selectionMode="single"
              label={t('scheduleEditor.startDate', 'Start Date')}
            />
          </div>
          <div data-testid="time-picker">
            <TimePickerAtom
              value={time}
              onChange={setTime}
              zone={zone}
              onZoneChange={setZone}
              className="schedules-editor__time"
            />
          </div>
        </FormRowAtom>
      </FormSectionAtom>

      {/* ZONE B: The Rule - Frequency, interval, and conditional inputs.
          EAINT-893: `variant="default"` (was `card`) removes the glossy
          panel background — handoff Board 6 shows this row on the same
          plain surface as the anchor row above. */}
      <FormSectionAtom variant="default" data-testid="zone-rule">
        {/* Frequency sentence row - includes frequency-specific controls inline.
            EAINT-893: wrapped in a light-gray rounded panel per handoff. */}
        <FormRowAtom
          align="center"
          gap="normal"
          wrap
          className="schedules-editor__rule-row"
        >
          <span className="schedules-editor__label">{t('scheduleEditor.every', 'Every')}</span>
          <div data-testid="interval-input">
            <InputNumberAtom
              value={interval}
              onChange={(v) => v !== null && setInterval(v)}
              min={1}
              showButtons
              className="schedules-editor__interval-input"
            />
          </div>
          <PrimeReactSingleSelect
            value={selectedFrequency}
            options={frequencyOptions}
            onChange={handleFrequencyChange}
            className="schedules-editor__frequency-dropdown"
            panelClassName="schedules-editor-panel"
            data-testid="frequency-dropdown"
          />

          {/* Weekly: Weekday picker inline */}
          {frequency === 'Weekly' && (
            <>
              <span className="schedules-editor__label">{t('scheduleEditor.on', 'on')}</span>
              <SegmentedControlAtom
                options={weekdayOptions}
                value={weekdays}
                onChange={(v) => setWeekdays(v as Weekday[])}
                mode="multi"
                variant="strip"
                useShortLabels
                errors={errors.weekdays ? [errors.weekdays] : []}
                ariaLabel={t('scheduleEditor.week.onTheseDays')}
                data-testid="weekday-picker"
              />
            </>
          )}

          {/* Monthly: Mode toggle inline */}
          {frequency === 'Monthly' && (
            <SegmentedControlAtom
              options={monthlyModeOptions}
              value={monthlyMode}
              onChange={(v) => setMonthlyMode(v as MonthlyMode)}
              mode="single"
              variant="strip"
              ariaLabel={t('scheduleEditor.monthlyMode', 'Monthly mode')}
              data-testid="monthly-mode"
            />
          )}

          {/* Yearly: Mode toggle inline */}
          {frequency === 'Yearly' && (
            <SegmentedControlAtom
              options={yearlyModeOptions}
              value={yearlyMode}
              onChange={(v) => setYearlyMode(v as YearlyMode)}
              mode="single"
              variant="strip"
              ariaLabel={t('scheduleEditor.yearlyMode', 'Yearly mode')}
              data-testid="yearly-mode"
            />
          )}
        </FormRowAtom>

        {/* Monthly: Additional inputs based on mode */}
        {frequency === 'Monthly' && monthlyMode === 'dayOfMonth' && (
          <FormRowAtom align="center" gap="normal">
            <span className="schedules-editor__label">{t('scheduleEditor.onDay', 'on day')}</span>
            <div data-testid="day-of-month">
              <InputNumberAtom
                value={dayOfMonth}
                onChange={(v) => v !== null && setDayOfMonth(v)}
                min={1}
                max={31}
                showButtons
                className="schedules-editor__day-input"
              />
            </div>
          </FormRowAtom>
        )}

        {frequency === 'Monthly' && monthlyMode === 'weekdayOfMonth' && (
          <FormRowAtom align="center" gap="normal" wrap>
            <span className="schedules-editor__label">{t('scheduleEditor.onThe', 'on the')}</span>
            <PrimeReactSingleSelect
              value={selectedMonthlyOrdinal}
              options={ordinalOptions}
              onChange={handleMonthlyOrdinalChange}
              className="schedules-editor__ordinal-dropdown"
              panelClassName="schedules-editor-panel"
              data-testid="monthly-ordinal"
            />
            <SegmentedControlAtom
              options={weekdayOptions}
              value={weekdayOfMonth.weekdays}
              onChange={(v) => setWeekdayOfMonth({ ...weekdayOfMonth, weekdays: v as Weekday[] })}
              mode="multi"
              variant="strip"
              useShortLabels
              errors={errors.weekdayOfMonth ? [errors.weekdayOfMonth] : []}
              ariaLabel={t('scheduleEditor.selectWeekdays', 'Select weekdays')}
              data-testid="monthly-weekdays"
            />
          </FormRowAtom>
        )}
        {/* lastDayOfMonth mode doesn't need additional inputs */}

        {/* Yearly: Additional inputs based on mode */}
        {frequency === 'Yearly' && yearlyMode === 'dayOfYear' && (
          <FormRowAtom align="center" gap="normal">
            <span className="schedules-editor__label">{t('scheduleEditor.on', 'on')}</span>
            <PrimeReactSingleSelect
              value={selectedYearlyMonth}
              options={monthOptions}
              onChange={handleYearlyMonthChange}
              className="schedules-editor__month-dropdown"
              panelClassName="schedules-editor-panel"
              data-testid="yearly-month"
            />
            <div data-testid="yearly-day">
              <InputNumberAtom
                value={dayOfYear.day}
                onChange={(v) => v !== null && setDayOfYear({ ...dayOfYear, day: v })}
                min={1}
                max={31}
                showButtons
                className="schedules-editor__day-input"
              />
            </div>
          </FormRowAtom>
        )}

        {frequency === 'Yearly' && yearlyMode === 'weekdayOfYear' && (
          <FormRowAtom align="center" gap="normal" wrap>
            <span className="schedules-editor__label">{t('scheduleEditor.onThe', 'on the')}</span>
            <PrimeReactSingleSelect
              value={selectedYearlyOrdinal}
              options={ordinalOptions}
              onChange={handleYearlyOrdinalChange}
              className="schedules-editor__ordinal-dropdown"
              panelClassName="schedules-editor-panel"
              data-testid="yearly-ordinal"
            />
            <SegmentedControlAtom
              options={weekdayOptions}
              value={weekdayOfYear.weekdays}
              onChange={(v) => setWeekdayOfYear({ ...weekdayOfYear, weekdays: v as Weekday[] })}
              mode="multi"
              variant="strip"
              useShortLabels
              errors={errors.weekdayOfYear ? [errors.weekdayOfYear] : []}
              ariaLabel={t('scheduleEditor.selectWeekdays', 'Select weekdays')}
              data-testid="yearly-weekdays"
            />
            <span className="schedules-editor__label">{t('scheduleEditor.of', 'of')}</span>
            <PrimeReactSingleSelect
              value={selectedYearlyWeekdayMonth}
              options={monthOptions}
              onChange={handleYearlyWeekdayMonthChange}
              className="schedules-editor__month-dropdown"
              panelClassName="schedules-editor-panel"
              data-testid="yearly-weekday-month"
            />
          </FormRowAtom>
        )}
      </FormSectionAtom>

      {/* ZONE C: Limits - End condition */}
      <FormSectionAtom variant="default" data-testid="zone-limits">
        <EndConditionAtom
          value={endCondition}
          onChange={setEndCondition}
          errors={errors.endCondition ? [errors.endCondition] : []}
          panelClassName="schedules-editor-panel"
          data-testid="end-condition"
        />
      </FormSectionAtom>

      {/* ZONE D: Feedback - Summary, preview, actions */}
      <FormSectionAtom variant="bordered" data-testid="zone-feedback">
        <div className="schedules-editor__summary" data-testid="schedule-summary">
          {renderScheduleSummary()}
        </div>

        <div className="schedules-editor__preview">
          {previewDates.length > 0 ? (
            <div className="schedules-editor__preview-chips">
              {previewDates.map((d) => (
                <ChipAtom
                  key={d.toISOString()}
                  label={formatPreviewDate(d)}
                  icon="pi pi-calendar"
                />
              ))}
            </div>
          ) : (
            <div data-testid="preview-button">
              <ButtonAtom
                label={t('scheduleEditor.preview', 'Preview')}
                icon="pi pi-eye"
                variant="secondary"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handlePreview();
                }}
              />
            </div>
          )}
        </div>

        {/* Hide Apply button in autoApply mode */}
        {editable && !autoApply && (
          <FormRowAtom justify="end" gap="normal">
            <div data-testid="apply-button">
              <ButtonAtom
                onClick={handleApplyChanges}
                disabled={isApplyDisabled}
                size="normal"
                label={t('scheduleEditor.applyChanges', 'Apply Changes')}
                icon="pi pi-check"
                variant="primary"
              />
            </div>
          </FormRowAtom>
        )}
      </FormSectionAtom>
    </div>
  );
};

export default SchedulesEditorMolecule;
